import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import { useSportsFilters } from 'projectSpecific/sports/useHelpers/useSportsFilters';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Label, Segment } from 'semantic-ui-react';
import { ParentContext } from '../../../../global/cnr/contexts/ParentContext';
import { formatItem, formatTypes } from '../../../../global/common/dateFormatting';
import { uniqueKey } from '../../../../global/common/keys';
import { sortObj } from '../../../../global/common/sorting';
import CalMonths from '../../../../global/components/calendar/CalMonths';
import { gEnums } from '../../../../global/enums/globalEnums';
import MotionDiv from '../../../../global/motions/MotionDiv';
import FullPageWrapper from '../../../../global/wrappers/FullPageWrapper';
import Wrapper from '../../../../global/wrappers/Wrapper';
import { SeasonMatchesContext } from '../../cnr/contexts/SeasonMatchesContext';
import { SportsSeasonContext, matchScheduleTypes } from '../../cnr/contexts/SportsSeasonContext';
import { _showTypes } from '../../cnr/reducers/SeasonMatchesReducer';
import { sportsShowTypes } from '../../cnr/reducers/SportsSeasonReducer';
import { sportsHelpers } from '../../cnr/reducers/reducerHelpers/sportsHelpers';
import { _sdm } from '../dashboard/SportsDashboard';
import ScheduleLegend from '../standings/ScheduleLegend';
import { SectionStandingz } from '../standings/SectionStandingz';
import MatchCreate from './MatchCreate';
import MatchDataUpdate from './MatchDataUpdate';
import MatchDateList from './MatchDateList';
import MatchList from './MatchList';
import MatchScheduleHeader from './MatchScheduleHeader';
import MatchWrapper, { sportsMatchViewTypes } from './MatchWrapper';

const _allowByes = true
const _labelSize = 'tiny'
const _useScrollIntoView = false

const matchHeaderIcons = {
  today: 'arrow right',
  todayS: 'arrow down',
  calendar: 'calendar alternate outline',
  list: 'list',
}

const _circulars = {
  matchCount: false,
  playoff: false,
  edit: false,
  today: true,
}

const MatchListSelected = (props) => {

  const { teamKey, dateKey, forOverview, forSummary } = props ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { currentSettings_state } = states
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { sportSettings } = aps_global ?? {}

  // authContext  
  const appUserAccess = useAppUserAccess()
  const { accessLevel } = appUserAccess ?? {}

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state, sportsSeason_handlers } = sportsSeasonContext ?? {}
  const { latestSeason, matches_info, seasonal_info, sportSettings_info, _sportSeasonSettings, parentDataKeys, _shows_sports } = sportsSeason_state ?? {}
  const { matchScheduleDisplayType, useScrollTo } = sportSettings_info ?? {}
  const { matches_groupInfo } = matches_info ?? {}
  const { matchDateKeys_count, matchDateKeys, matchDateGroups } = matches_groupInfo ?? {}
  const { matches_seasonal } = parentDataKeys ?? {}
  const { startHour } = _sportSeasonSettings ?? {}

  const { getTeamsAvailableByAllDates } = useSportsFilters()

  // console.log('matches_groupInfo', matches_groupInfo)
  const getMdt = useMemo(() => {
    if (!teamKey) return [];

    const filteredMatches = _.flatMap(matchDateGroups, item =>
      _.flatMap(item.matches, match =>
        match.home?.id === teamKey || match.away?.id === teamKey ? [match] : []
      )
    );

    return _.map(_.groupBy(filteredMatches, 'startDate'), (matches, matchDate) => ({
      matchDate,
      matches
    }));
  }, [teamKey, matchDateGroups]);

  // seasonMatchesContext
  const seasonMatchesContext = useContext(SeasonMatchesContext)
  const { seasonMatches_state, seasonMatches_handlers } = seasonMatchesContext ?? {}
  const {
    _shows_matches,
    commonMatches,
    dateMatches,
    dateMatchResults,
    forPlayoff,
    isTournament,
    lastDatesSelected,
    levelDatesGroup,
    levelDatesKey,
    levelKey: levelKey_selected,
    matchScheduleType,
    pathViews,
    playoffDates,
    poolMatches,
    scrollToDate,
    scrollToDateIndex,
    sepBySection,
    teamMatches,
    tournamentType,
  } = seasonMatches_state

  const {
    matchResults: showMatchResults,
  } = _shows_sports ?? {}

  const {
    teamCalendar: showTeamCalendar,
    calendar: showCalendar,
    expand: showExpand,
    newMatch: showNewMatch,
  } = _shows_matches ?? {}

  const { levelDateMatches, sectionDateMatches, favMatches, matchDate } = dateMatchResults ?? {}

  let showDateHeader;
  let headerFormatType;

  switch (matchScheduleDisplayType) {
    case gEnums.matchScheduleDisplayTypes.sport_allDates:
    case gEnums.matchScheduleDisplayTypes.clickedDates:
      showDateHeader = true
      headerFormatType = formatTypes.fullDate
      break;
    default:
      headerFormatType = formatTypes.shorterDate
  }

  const scheduleTypes = useMemo(() => ({
    district: pathViews?.districts && !pathViews.matches && !pathViews.sports && !pathViews.teams,
    match: pathViews?.matches ?? false,
    sport: pathViews?.sports ?? false,
    team: teamKey || (pathViews && pathViews.teams) ? true : false,
    common: !!commonMatches,
    date: !!dateKey,
  }), [pathViews, teamKey, commonMatches, dateKey]);


  // const showByes = _allowByes && (scheduleTypes.team)
  const showByes = useMemo(() => _allowByes && scheduleTypes.team, [scheduleTypes, _allowByes]);

  const [availableDates, setAvailableDates] = useState()
  const [availableTeams, setAvailableTeams] = useState()
  const [matchesReady] = useState(true)
  const [selectedAvailableDate, setSelectedAvailableDate] = useState()
  const [showMissingOnly, setShowMissingOnly] = useState()
  const [showEditDate] = useState()
  const [matchesLoaded, setMatchesLoaded] = useState()
  const [scrollDiv, setScrollDiv] = useState()
  const [scrollDivId, setScrollDivId] = useState()

  let _selectedDate = lastDatesSelected && lastDatesSelected[levelKey_selected] ? lastDatesSelected[levelKey_selected] : levelDatesKey
  if (scrollToDate) {
    _selectedDate = scrollToDate
  }

  const scollToRef = useRef(); // eslint-disable-next-line     
  const parentDivRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setMatchesLoaded(true), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_selectedDate, levelKey_selected]);

  useEffect(() => {
    if (matchesLoaded && _selectedDate && useScrollTo && scollToRef && scollToRef.current && !forPlayoff) {
      if (scollToRef.current) {
        const selectedDiv = scollToRef.current
        if (selectedDiv && selectedDiv.getAttribute('las') === levelKey_selected) {
          setScrollDiv(selectedDiv)
          setScrollDivId(selectedDiv.id)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToDateIndex, scrollToDate, _selectedDate, matchesLoaded, levelKey_selected]);

  useEffect(() => {
    if (scrollDiv && _useScrollIntoView) {
      setTimeout(function () {
        scrollDiv.scrollIntoView({
          behavior: "instant",
        });
        if (!showExpand) {
          // seasonMatches_handlers.handleShow_matches(_showTypes.expand, null, true)
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToDateIndex, scrollDivId, scrollDiv]);

  /**
 * 
 * @param {date} matchDate 
 * @handles the date click from the calendar
 */
  const handleCalSelectedDate = (d) => {
    if (d === 'Missing') {
      setShowMissingOnly(!showMissingOnly)
    } else {
      seasonMatches_handlers.handle_setVisibleDates(levelKey_selected, d)
    }
    seasonMatches_handlers.handleShow_matches(_showTypes.calendar)
  }

  /**
   * 
   * @param {date} matchDate 
   * @handles the date header click
   */
  const handleDateHeaderClick = (matchDate) => seasonMatches_handlers.handle_setVisibleDates(levelKey_selected, matchDate)

  /**
   * 
   * @handles the scroll of the matches into view
   */

  const handleGetAvailableTeams = () => {
    if (availableTeams) {
      setAvailableTeams()
    } else if (availableDates) {
      setAvailableDates()
    } else {
      const ta = getTeamsAvailableByAllDates()
      setAvailableDates(ta)
      // setAvailableTeams(_.sortBy(ta, 'name'))
    }
  }

  const handleSelectedAvailableDate = (sd) => setSelectedAvailableDate(sd === selectedAvailableDate ? null : sd)

  const handleEditDate = (e, md) => {
    e.stopPropagation()
    const opts = { sportsShowType: sportsShowTypes.matchResults, matchDate: md }
    sportsSeason_handlers.handleShow_sportsElem(opts)
  }

  const singleMatch = (segs, match, showFavs, keyOrIndex) => {

    const { _status, key, id } = match ?? {}
    const { isBye } = _status ?? {}

    let keyy = key ? key : id
    if (!keyy) { keyy = keyOrIndex }

    if (!isBye || showByes) {
      const m = <MatchWrapper
        key={uniqueKey('mls', 'mw', keyy)}
        sportsMatchViewType={sportsMatchViewTypes.single}
        scheduleTypes={scheduleTypes}
        match={match}
        tournamentType={tournamentType}
        teamKey={teamKey}
        forOverview={forOverview}
        forSummary={forSummary}
      />
      segs.push(m)
    }
  }

  const matchList = (matches, showFavs, isFuture) => {

    let segs = []
    let _matches = matches;

    const _groupedMatches = []
    _groupedMatches.push({
      matches: _matches,
      date: '01/01/2025'
    })

    if (_.isArray(_matches)) {
      if (_matches && _matches.length > 0) {
        _.forEach(_matches, (match, index) => {
          singleMatch(segs, match, showFavs, index)
        })
      }
    } else {
      if (_matches && Object.keys(_matches).length > 0) {
        _.forEach(_matches, (match, key) => {
          singleMatch(segs, match, showFavs, key)
        })
      }
    }

    return segs.length > 0 ? segs : <Segment key={uniqueKey('mls', 'ml')} className={'seg-ph-full'} placeholder style={{ height: '100%', textAlign: 'center', fontWeight: 'bold' }}>{isFuture ? 'No Upcoming Matches' : 'No Matches'}</Segment>
  }

  const sectionDateSegs = (matches, segsArr, levelKey, matchDate, index) => {
    segsArr.push(<MatchDateList
      _levelKey={levelKey ? levelKey : levelKey_selected}
      accessLevel={accessLevel}
      forOverview={forOverview}
      forSummary={forSummary}
      index={index}
      matchDate={matchDate}
      matches={matches}
      scheduleTypes={scheduleTypes}
      scollToRef={scollToRef}
      teamKey={teamKey}
    />)
  }

  const favSegs = (fs) => {
    if (favMatches) {
      const favMatchSegs = matchList(favMatches)
      fs.unshift(<div key={uniqueKey('mls', 'lds', 'f')} className={'schedule-section fav'}>
        <Divider horizontal>{'Following'}</Divider>
        <div key={uniqueKey('mls', 'lds', 'fst')} className={'schedule-section-teams'}>{favMatchSegs}</div>
      </div>)
    }
  }

  const todaysMatches = () => {

    const levelSegsElems = []
    const _favSegElems = []

    favSegs(_favSegElems)
    if (sepBySection) {
      switch (matchScheduleDisplayType) {
        case gEnums.matchScheduleDisplayTypes.sport_allDates:
        case gEnums.matchScheduleDisplayTypes.clickedDates:
          if (levelDatesGroup) {
            let index = 0
            _.forEach(levelDatesGroup, (levelGroupDate, dgKey) => {
              const { matchDate, matches } = levelGroupDate ?? {}
              sectionDateSegs(matches, levelSegsElems, null, matchDate, index)
              index++
            })
          }
          break;
        case gEnums.matchScheduleDisplayTypes.levelDates:
          levelSegsElems.push(<div>TEST</div>)
          break;
        default:
          sectionDateSegs(sectionDateMatches, levelSegsElems)
      }
    } else if (levelDateMatches) {
      Object.keys(levelDateMatches).forEach(levelKey => {
        const sectionSegs = []
        let sections = _.groupBy(levelDateMatches[levelKey], 'sections')
        const ss = sortObj(sections)
        Object.keys(ss).forEach(sectionKey => {
          const _sectionMatches = sections[sectionKey]
          sectionDateSegs(_sectionMatches, sectionSegs, levelKey)
        })

        levelSegsElems.push(<div key={uniqueKey('mls', 'tm')} className={'schedule-level'}>
          <div key={uniqueKey('mls', 'tm', 'h')} className={'schedule-level-header'}>{levelKey}</div>
          <div key={uniqueKey('mls', 'tm', 'ls')} className={'schedule-level-section'}>{sectionSegs}</div>
        </div>)
      })
    }

    let cn = 'schedule-dates'
    if (!matchesReady) { cn += ' nr' }

    return <div key={uniqueKey('mls', 'sdm')} className={cn}>
      {showDateHeader && <MatchScheduleHeader />}
      <div className={'schedule-dates-wrapper'}>
        <MotionDiv>
          <div key={uniqueKey('mls', 'sl')} ref={parentDivRef} className={'schedule-levels'}>{_favSegElems}{levelSegsElems}</div>
          <ScheduleLegend />
        </MotionDiv>
      </div>
    </div>
  }

  const todaysPlayoffMatches = () => {
    const sectionMatchesSorted = _.sortBy(sectionDateMatches, 'matchNumber')
    const sectionMatchSegs = matchList(sectionMatchesSorted)
    return <div key={uniqueKey('mls', 'tpm')} className={'schedule-dates'}>
      <div key={uniqueKey('mls', 'tpm', 'sl')} ref={parentDivRef} className={'schedule-levels'}>{sectionMatchSegs}</div>
    </div>
  }

  const wrapper_availableTeams = () => <Wrapper
    content={<SectionStandingz
      sectionOpts={{ availableTeams }}
      caption={'Idle Teams'}
      levelKey={levelKey_selected}
    />}
  />

  const wrapper_availableDates = () => {
    const dateItems = []
    if (availableDates) {
      Object.keys(availableDates).forEach(adk => {
        const availableTeams = availableDates[adk]
        const _ateams = _.sortBy(availableTeams, 'name')
        const teamCount = _ateams ? _ateams.length : 0
        dateItems.push(<div className={'available-teams'}>
          <div onClick={() => { handleSelectedAvailableDate(adk) }}>{formatItem(formatTypes.fullDate, adk)}<Label size={_labelSize} color='blue'>{teamCount}</Label></div>
          {selectedAvailableDate === adk && <SectionStandingz sectionOpts={{ availableTeams: _ateams }} caption={'Idle Teams'} />}
        </div>)
      })
    }
    return <Wrapper
      content={dateItems}
    />
  }

  const fpw_availableTeams = () => <FullPageWrapper
    content={wrapper_availableTeams()}
    handleCancel={handleGetAvailableTeams}
    topperCaption={'Idle Teams'}
    topperCaption2={matchDate}
  ></FullPageWrapper>

  const fpw_availableDates = () => <FullPageWrapper
    content={wrapper_availableDates()}
    handleCancel={handleGetAvailableTeams}
    topperCaption={'Idle Teams'}
    topperCaption2={matchDate}
  ></FullPageWrapper>

  const wrapper_calendar = () => {
    let _matchDateKeys = matchDateKeys
    let _matchDateKeys_count = matchDateKeys_count
    let _showToday = true
    let x;
    if (showTeamCalendar && pathViews.teams) {
      x = getMdt(pathViews.teams)
      const mdks = sportsHelpers.getMatchDateKeys(x)
      const { matchDateKeys: k, matchDateKeys_count: c } = mdks ?? {}
      _matchDateKeys = k
      _matchDateKeys_count = c
      _showToday = false
    }
    const sidebarItem = {
      items: _matchDateKeys,
      itemz: _matchDateKeys_count,
      itemsData: x,
      keyAs: "matchDates",
      showToday: _showToday,
    }

    return <Wrapper
      content={<CalMonths
        sidebarItem={sidebarItem}
        handleClick={handleCalSelectedDate}
        handleClose={seasonMatches_handlers.handleShow_matches}
        teamKey={pathViews.teams}
      />}
      wrapperCss={{ container: 'app-dark-mode' }}
    />
  }

  const fpw_calendar = () => <FullPageWrapper
    content={wrapper_calendar()}
    handleCancel={seasonMatches_handlers.handleShow_matches}
    cancelOptions={_showTypes.calendar}
    topperCaption={'Match Calendar'}
  ></FullPageWrapper>

  const fpw_results = () => <FullPageWrapper
    content={<MatchDataUpdate fromApp={true} forSportsManagement={true} sportsDataMode={_sdm.sports.seasonResults} />}
    handleCancel={seasonMatches_handlers.handleShow_matches}
    cancelOptions={_showTypes.matchResults}
    topperCaption={'Match Results'}
    topperCaption2={latestSeason + ' : ' + matches_seasonal}
  ></FullPageWrapper>

  const fpw_editDate = () => {
    const mm = _.find(levelDatesGroup, { matchDate: showEditDate })
    return <FullPageWrapper
      content={<MatchList matches={mm && mm.matches} allowEdits={true} showEditDate={showEditDate} />}
      handleCancel={handleEditDate}
      cancelOptions={_showTypes.matchResults}
      topperCaption={'Date Matches'}
    ></FullPageWrapper>
  }

  const memoizedContent = useMemo(() => {
    if (showEditDate) {
      return fpw_editDate();
    } else if (showMatchResults) {
      return fpw_results();
    } else if (showCalendar || showTeamCalendar) {
      return fpw_calendar();
    } else {
      if (availableTeams) {
        return fpw_availableTeams();
      } else if (availableDates) {
        return fpw_availableDates();
      } else if (isTournament) {
        const pms = _.sortBy(poolMatches, "matchNumber");
        return <div className="tournament-match-container">{matchList(pms)}</div>;
      } else {
        if (scheduleTypes.date) {
          return <div>TEST</div>;
        } else if (scheduleTypes.common) {
          return matchList(commonMatches);
        } else if (scheduleTypes.match && !matchScheduleType) {
          return matchList(dateMatches);
        } else if (scheduleTypes.team) {
          switch (matchScheduleType) {
            case matchScheduleTypes.latest:
            case matchScheduleTypes.next:
              if (teamMatches && teamMatches.length > 0) {
                return matchList(teamMatches);
              }
              break;
            default:
              if (dateMatches) {
                const xxx = Object.values(teamMatches);
                const today_before = new Date().setHours(startHour, 0, 0, 0);
                const today_after = new Date().setHours(startHour, 0, 0, 0);
                const matchesBeforeToday = xxx.filter(
                  (match) =>
                    new Date(match.startDate).setHours(startHour, 0, 0, 0) <
                    today_before
                );
                const matchesTodayAndBeyond = xxx.filter(
                  (match) =>
                    new Date(match.startDate).setHours(startHour, 0, 0, 0) >=
                    today_after
                );
                return (
                  <div>
                    {matchesBeforeToday && matchList(matchesBeforeToday)}
                    {matchesTodayAndBeyond && (
                      <div>
                        {matchList(matchesTodayAndBeyond, null, true)}
                      </div>
                    )}
                  </div>
                );
              } else {
                return matchList(teamMatches);
              }
          }
        } else if (playoffDates) {
          return todaysPlayoffMatches();
        } else {
          return showNewMatch ? (
            <MatchCreate
              handleSetShowNewMatch={seasonMatches_handlers.handleShow_matches}
              shows={{ showTeams: true, showDate: true, showLocation: false }}
              matchDate={matchDate}
            />
          ) : (
            todaysMatches()
          );
        }
      }
    }
  }, [
    showEditDate,
    showMatchResults,
    showCalendar,
    showTeamCalendar,
    availableTeams,
    availableDates,
    isTournament,
    poolMatches,
    scheduleTypes,
    matchScheduleType,
    matchScheduleTypes,
    teamMatches,
    dateMatches,
    commonMatches,
    sepBySection,
    // playoffDates,
    showNewMatch,
    // matchDate,
    // seasonMatches_handlers,
    // startHour,
    matchScheduleDisplayType
  ]);


  return memoizedContent

}

export default MatchListSelected
